import { Injectable, inject, signal } from '@angular/core';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';
import { cloneDeep } from '@fi-app/shared/utils/clone-deep';
import {
    IOnboardingCompletionStep,
    IOnboardingCompletionUserStep,
    IOnboardingStepCompletionConfig,
} from '../fi-onboarding-completion';
import { FiOnboardingCompletionStateType } from '../fi-onboarding-completion-enums';
import { FiOnboardingCompletionHelperService } from '../fi-onboarding-completion-helper.service';

export interface IFiOnboardingStepCompletionDialogInitData {
    onboardingId: number;
    userId: number;
    step: IOnboardingCompletionStep;
    userCompletionStep: IOnboardingCompletionUserStep;
    nextStepCompletionState: FiOnboardingCompletionStateType | undefined;
    isReportMode?: boolean;
}

@Injectable()
export class FiOnboardingStepCompletionDialogState {
    private readonly completionHelper = inject(FiOnboardingCompletionHelperService);
    private readonly onboardingHelper = inject(FiOnboardingHelperService);

    readonly userCompletionStep = signal<IOnboardingCompletionUserStep>(undefined);

    step: IOnboardingCompletionStep;
    stepCompletionConfig: IOnboardingStepCompletionConfig;
    dialogTitle: string;
    saveButtonTextId: string;

    readonly isSaveButtonHidden = signal<boolean>(true);
    readonly isSaveButtonDisabled = signal<boolean>(true);
    readonly isCloseButtonHidden = signal<boolean>(false);
    readonly isReviewMode = signal<boolean>(false);

    private readonly _isReportMode = signal<boolean>(false);
    readonly isReportMode = this._isReportMode.asReadonly();

    private isQuizStep = false;

    init(data: IFiOnboardingStepCompletionDialogInitData): void {
        this.step = data.step;
        const isTodoListStep = this.onboardingHelper.isTodoListStep(data.step.ObjectType);
        this.isQuizStep = this.onboardingHelper.isQuizStep(data.step.ObjectType);

        const initialUserCompletionState = data.userCompletionStep.CompletionState;
        const built = this.buildUserCompletionStep(data, isTodoListStep);
        this.userCompletionStep.set(built);

        this.stepCompletionConfig = {
            userId: data.userId,
            onboardingId: data.onboardingId,
            onboardingStep: this.step,
            userCompletionStep: built,
            nextStepCompletionState: data.nextStepCompletionState,
            initialUserCompletionState,
            isQuizStep: this.isQuizStep,
            isTodoListStep,
        };

        this._isReportMode.set(data.isReportMode);

        this.dialogTitle = this.isQuizStep ? this.step.Quiz.Name : this.step.Name;
        this.saveButtonTextId = this.completionHelper.saveButtonTextIds[this.step.ObjectType] ?? 'post.mark_as_read';

        const initial = this.completionHelper.getInitialSubmitButtonState(this.step, built);
        this.isSaveButtonHidden.set((initial?.isSaveButtonHidden ?? true) || data.isReportMode);
        this.isSaveButtonDisabled.set((initial?.isSaveButtonDisabled ?? true) || data.isReportMode);
    }

    setUserCompletionStep(next: IOnboardingCompletionUserStep): void {
        this.userCompletionStep.set(next);
        this.stepCompletionConfig.userCompletionStep = next;
        if (this.isQuizStep) {
            this.refreshQuizSubmitButtonDisabled();
        }
    }

    updateUserCompletionStep(updater: (prev: IOnboardingCompletionUserStep) => IOnboardingCompletionUserStep): void {
        this.setUserCompletionStep(updater(this.userCompletionStep()));
    }

    getCloseButtonTextId(): string {
        if (this.isQuizProgressStarted()) {
            return 'common.save_&_close';
        }
        if (this.isReviewMode()) {
            return 'back';
        }
        return 'common.close';
    }

    refreshQuizSubmitButtonDisabled(): void {
        if (!this.step || !this.onboardingHelper.isQuizStep(this.step.ObjectType)) {
            return;
        }
        this.isSaveButtonDisabled.set(
            this.completionHelper.isSubmitButtonDisabledOnQuizAnswerChange(this.step, this.userCompletionStep()),
        );
    }

    setSaveButtonHidden(hidden: boolean): void {
        this.isSaveButtonHidden.set(hidden);
    }

    setSaveButtonDisabled(disabled: boolean): void {
        this.isSaveButtonDisabled.set(disabled);
    }

    setCloseButtonHidden(hidden: boolean): void {
        this.isCloseButtonHidden.set(hidden);
    }

    enterQuizReviewMode(): void {
        this.isReviewMode.set(true);
    }

    exitQuizReviewMode(): void {
        this.isReviewMode.set(false);
    }

    private buildUserCompletionStep(
        data: IFiOnboardingStepCompletionDialogInitData,
        isTodoListStep: boolean,
    ): IOnboardingCompletionUserStep {
        const userCompletionStep = cloneDeep(data.userCompletionStep);
        if (isTodoListStep) {
            return this.completionHelper.normalizeTodoListCompletionStep(
                data.step.TodoList.Sections,
                userCompletionStep,
            );
        }
        return userCompletionStep;
    }

    private isQuizProgressStarted(): boolean {
        const step = this.userCompletionStep();
        return (
            this.isQuizStep &&
            !this.completionHelper.isQuizStepCompletionNotCompleted(step) &&
            this.quizAnswersAreNotEmpty(step) &&
            !this.isSaveButtonHidden()
        );
    }

    private quizAnswersAreNotEmpty(userStep: IOnboardingCompletionUserStep): boolean {
        return userStep.QuizCompletion.Answers.some((answer) => answer.Value?.length > 0);
    }
}
